import React, { useCallback, useEffect, useState } from "react";
import Select from "./components/Select";
import Spacer from "./components/Spacer";
import Toggle from "../common/Toggle";
import Collapsible from "react-collapsible";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import Input from "./components/Input";
import { LOCAL_PREFIX } from "../../utils/constants";
import ConfirmModal from "../common/ConfirmModal";
import { getHighestLevelWithPrefix } from "./utils/parseProblems";
import { levelToColor, levelToIconName } from "./components/ProblemIndicator";

const EMERGENCE_OPTIONS = ["Pre-emergence", "Post-emergence"];

const NITROGEN_UNIT_OPTIONS = ["Lbs/Ac", "Gallons/Ac"];

const INHIBITOR_OPTIONS = [
  "Nitrification Inhibitor",
  "Urease Inhibitor",
  "Nitrification & Urease Inhibitor",
  "Biologic",
];

function getConfirmMessage(toDelete) {
  if (toDelete.isSaved) {
    return `This will remove "${toDelete.name}". After saving you will not be able to recover this change. If unsaved, you can reload the page to recover this application.`;
  }

  return `This will permanently remove "${toDelete.name}". This cannot be undone.`;
}

export default function NitrogenApplicationSection({
  index = 0,
  crop,
  initialData,
  onRemove,
  onChange,
  formProblems,
  canRemoveFirst,
}) {
  // NOTE: This is a good candidate for useReducer, but I'm sticking with state for now.
  const [date, setDate] = useState(initialData.date ?? "");
  const [nitrogenApplied, setNitrogenApplied] = useState(initialData.nitrogenApplied ?? "");
  const [emergenceType, setEmergenceType] = useState(initialData.emergenceType ?? "");

  const [type, setType] = useState(initialData.type ?? "");
  const [amount, setAmount] = useState(initialData.amount ?? "");
  const [units, setUnits] = useState(initialData.units ?? "");

  const [nitrogen, setNitrogen] = useState(initialData.nitrogen ?? "");
  const [phosphate, setPhosphate] = useState(initialData.phosphate ?? "");
  const [potassium, setPotassium] = useState(initialData.potassium ?? "");

  const [density, setDensity] = useState(initialData.density ?? "");

  const [isAdvanced, setIsAdvanced] = useState(() => {
    if (nitrogenApplied !== "") {
      // If nitrogen applied has a value, this form isn't advanced.
      return false;
    }

    // If any of the advanced fields are defined, this is advanced.
    if (type !== "" || amount !== "" || units !== "" || density !== ""
      || nitrogen !== "" || phosphate !== "" || potassium !== "") {
      return true;
    }

    // Otherwise the form is empty, so not advanced.
    return false;
  });

  const [inhibitorUsed, setInhibitorUsed] = useState(initialData.inhibitorUsed ?? false);
  const [inhibitorType, setInhibitorType] = useState(initialData.inhibitorType ?? false);

  const prefix = `${crop.id}-nitro-${initialData.id}`;
  const basicName = `${prefix}-nitrogen-applied`;

  const [toDelete, setToDelete] = useState(undefined);

  const setAndClearProblem = useCallback((setter, value, field, clearNitroApplied = false) => {
    const i = formProblems.findIndex((p) => p.field === field);
    if (i >= 0) {
      formProblems.splice(i, 1);
    }

    // A special case for advanced fields so they can also clear the alternate error.
    if (clearNitroApplied) {
      const n = formProblems.findIndex((p) => p.field === basicName);
      if (n >= 0) {
        formProblems.splice(i, 1);
      }
    }

    setter(value);
  }, [formProblems]);

  useEffect(() => {
    const id = initialData.id;
    onChange({
      id,
      date,
      emergenceType,
      isAdvanced,
      nitrogenApplied: isAdvanced ? "" : nitrogenApplied,
      type: isAdvanced ? type : "",
      amount: isAdvanced ? amount : "",
      units: isAdvanced ? units : "",
      nitrogen: isAdvanced ? nitrogen : "",
      phosphate: isAdvanced ? phosphate : "",
      potassium: isAdvanced ? potassium : "",
      density: isAdvanced ? density : "",
      inhibitorUsed,
      inhibitorType: inhibitorUsed ? inhibitorType : "",
    });
  }, [
    isAdvanced, date, nitrogenApplied, emergenceType, type, amount, units,
    nitrogen, phosphate, potassium, density, inhibitorUsed, inhibitorType,
  ]);

  const problemLevel = getHighestLevelWithPrefix(formProblems, prefix, true);

  return (<>
    <div className={`
      w-full p-0.5 bg-slate-100 rounded-md
    `}>
      <Collapsible
        className="p-0.5 bg-slate-100 rounded-md w-full"
        openedClassName="p-0.5 bg-slate-100 rounded-md w-full"
        contentOuterClassName="w-full max-w-full"
        contentInnerClassName="flex flex-col px-2 pb-1"
        triggerClassName="group"
        triggerOpenedClassName="group"
        trigger={(
          <div className="flex items-center gap-2 pl-2">
            <FontAwesomeIcon
              icon="chevron-circle-down"
              className="group-[.is-closed]:-rotate-90 transition-all text-slate-500"
            />
            <h4>
              Nitrogen Application {index + 1}
              {date && ` (${moment(date).format("MM/DD/YYYY")})`}
              {problemLevel !== "" && (
                <FontAwesomeIcon
                  icon={levelToIconName(problemLevel)}
                  className={`ml-1 ${levelToColor(problemLevel)}`}
                  data-tooltip-id="invalid-application-tooltip"
                />
              )}
            </h4>
            <Spacer />
            {(index > 0 || canRemoveFirst) && <button
              title="Remove this application"
              className={`
                bg-red-500 text-white h-5 w-5 rounded-full flex items-center justify-center
                text-lg mr-4 group-[.is-closed]:hidden
              `}
              onClick={(event) => {
                event.stopPropagation();
                setToDelete({
                  name: `Nitrogen Application ${index + 1}`,
                  confirm: onRemove,
                  isSaved: !initialData.id.startsWith(LOCAL_PREFIX),
                });
              }}
            >
              -
            </button>}
          </div>
        )}
        transitionTime={150}
        easing="ease-in"
        open
      >
        <div className="px-2 pb-1">
          <div className="flex gap-4">
            <Input
              label="Date"
              name={`${prefix}-date`}
              formErrors={formProblems}
              type="date"
              value={date}
              onChange={(value) => setAndClearProblem(setDate, value, `${prefix}-date`)}
            />
            <Select
              label="Emergence Type"
              value={emergenceType}
              name={`${prefix}-emergence`}
              formErrors={formProblems}
              options={EMERGENCE_OPTIONS.map((v) => ({ label: v, value: v }))}
              onChange={({ value }) => setAndClearProblem(setEmergenceType, value, `${prefix}-emergence`)}
            />
            <Input
              label="Nitrogen Applied"
              name={basicName}
              formErrors={formProblems}
              // Never show an error on this field if using advanced configuration
              error={isAdvanced ? null : undefined}
              type="number"
              value={nitrogenApplied}
              onChange={(value) => setAndClearProblem(setNitrogenApplied, value, basicName)}
              suffix="lbs/ac"
              disabled={isAdvanced}
            />
          </div>

          <div className="flex flex-col">
            <Toggle
              label="Advanced Configuration"
              name={`${prefix}-advanced`}
              formErrors={formProblems}
              value={isAdvanced}
              onChange={(value) => setAndClearProblem(setIsAdvanced, value, `${prefix}-advanced`)}
              className="my-4 mx-2"
            />
            {isAdvanced && <>
              <div>
                <Input
                  label="Type (anhydrous ammonia)"
                  name={`${prefix}-type`}
                  formErrors={formProblems}
                  error={isAdvanced ? formProblems?.find((p) => p.field === basicName) : undefined}
                  value={type}
                  onChange={(value) => setAndClearProblem(setType, value, `${prefix}-type`, true)}
                />
              </div>
              <div className="flex gap-4">
                <Input
                  label="Amount"
                  name={`${prefix}-amount`}
                  formErrors={formProblems}
                  error={isAdvanced ? formProblems?.find((p) => p.field === basicName) : undefined}
                  type="number"
                  value={amount}
                  onChange={(value) => setAndClearProblem(setAmount, value, `${prefix}-amount`, true)}
                />
                <Select
                  label="Amount Units"
                  name={`${prefix}-units`}
                  formErrors={formProblems}
                  error={isAdvanced ? formProblems?.find((p) => p.field === basicName) : undefined}
                  value={units}
                  options={NITROGEN_UNIT_OPTIONS.map((v) => ({ label: v, value: v }))}
                  onChange={({ value }) => setAndClearProblem(setUnits, value, `${prefix}-units`, true)}
                />
              </div>
              <div className="flex gap-4">
                <Input
                  label="Nitrogen"
                  name={`${prefix}-n-percent`}
                  formErrors={formProblems}
                  error={isAdvanced ? formProblems?.find((p) => p.field === basicName) : undefined}
                  type="number"
                  suffix="%"
                  value={nitrogen}
                  onChange={(value) => setAndClearProblem(setNitrogen, value, `${prefix}-n-percent`)}
                />
                <Input
                  label="Phosphate"
                  name={`${prefix}-p-percent`}
                  formErrors={formProblems}
                  error={isAdvanced ? formProblems?.find((p) => p.field === basicName) : undefined}
                  type="number"
                  suffix="%"
                  value={phosphate}
                  onChange={(value) => setAndClearProblem(setPhosphate, value, `${prefix}-p-percent`, true)}
                />
                <Input
                  label="Potassium"
                  name={`${prefix}-k-percent`}
                  formErrors={formProblems}
                  error={isAdvanced ? formProblems?.find((p) => p.field === basicName) : undefined}
                  type="number"
                  suffix="%"
                  value={potassium}
                  onChange={(value) => setAndClearProblem(setPotassium, value, `${prefix}-k-percent`, true)}
                />
              </div>
              <div>
                <Input
                  label="Density"
                  name={`${prefix}-density`}
                  formErrors={formProblems}
                  error={isAdvanced ? formProblems?.find((p) => p.field === basicName) : undefined}
                  type="number"
                  suffix="lbs/gallon"
                  value={density}
                  onChange={(value) => setAndClearProblem(setDensity, value, `${prefix}-density`, true)}
                />
              </div>
            </>}
          </div>

          <div className="flex gap-4">
            <Toggle
              label="Inhibitor Used"
              name={`${prefix}-inhibitor-used`}
              formErrors={formProblems}
              value={inhibitorUsed}
              onChange={(value) => setAndClearProblem(setInhibitorUsed, value, `${prefix}-inhibitor-used`)}
              className="my-4 mx-2"
            />
            {inhibitorUsed && <Select
              label="Inhibitor Type"
              name={`${prefix}-inhibitor-type`}
              formErrors={formProblems}
              value={inhibitorType}
              options={INHIBITOR_OPTIONS.map((v) => ({ label: v, value: v }))}
              onChange={({ value }) => setAndClearProblem(setInhibitorType, value, `${prefix}-inhibitor-type`)}
            />}
          </div>
        </div>
      </Collapsible>
    </div>
    <ConfirmModal
      isOpen={!!toDelete}
      title="Confirm Delete Nitrogen Application"
      message={toDelete ? getConfirmMessage(toDelete) : ""}
      confirmButtonLabel="Yes, Delete"
      toggle={() => setToDelete(undefined)}
      onConfirm={() => {
        toDelete.confirm();
        setToDelete(undefined);
      }}
      secondaryButtonText="Cancel"
      warning
    />
  </>);
}
